import React from 'react';
import { Helmet } from 'react-helmet';

// URL path mappings for hreflang (standard pages)
const pathMappings = {
  '/': '/',  // Homepage maps to itself in both languages
  '/arbeiten': '/',  // Old arbeiten URL redirects to root
  '/portfolio': '/',  // Old portfolio URL redirects to root
  '/ueber_mich': '/about',
  '/about': '/ueber_mich',
  '/kontakt': '/contact',
  '/contact': '/kontakt',
  '/impressum': '/imprint',
  '/imprint': '/impressum',
  '/datenschutz': '/privacy-policy',
  '/privacy-policy': '/datenschutz',
};

// Helper to get alternate artwork URL
const getAlternateArtworkUrl = (path, currentLang) => {
  // Match /werk/slug or /artwork/slug
  const werkMatch = path.match(/^\/werk\/(.+)$/);
  const artworkMatch = path.match(/^\/artwork\/(.+)$/);
  
  if (werkMatch) {
    // German to English: /werk/slug -> /artwork/slug
    return `/artwork/${werkMatch[1]}`;
  } else if (artworkMatch) {
    // English to German: /artwork/slug -> /werk/slug
    return `/werk/${artworkMatch[1]}`;
  }
  return path;
};

// Artist data for structured data - reusable across pages
const artistData = {
  '@type': 'Person',
  '@id': 'https://atelier-wpb.com/#person',
  name: 'Waltraud Pfister-Bläske',
  alternateName: 'Atelier WPB',
  description: 'Abstrakte Malerei zwischen Starnberger See und Italien. Die Essenz der Natur durch abstrakte Expression einfangen.',
  url: 'https://atelier-wpb.com',
  image: 'https://atelier-wpb.com/images/profilepic_small.webp',
  jobTitle: 'Künstlerin',
  knowsAbout: ['Abstrakte Malerei', 'Ölmalerei', 'Abstrakte Kunst', 'Expressionismus'],
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Johann-Biersack-Straße 27b',
    addressLocality: 'Feldafing',
    postalCode: '82340',
    addressRegion: 'Bayern',
    addressCountry: 'DE'
  },
  email: 'atelier.wpb.feldafing@gmail.com',
  sameAs: [
    'https://www.instagram.com/atelier_wpb/'
  ]
};

// This component adds structured data and SEO meta tags to any page
function SEOHelmet({ pageType, title, description, imageUrl, path, language, author, datePublished, keywords }) {
  const baseUrl = 'https://atelier-wpb.com';
  const url = `${baseUrl}${path || ''}`;
  const defaultImage = `${baseUrl}/images/og-image.jpg`;
  
  // ISO 8601 dates for structured data (with timezone)
  const dateCreated = '2024-01-01T00:00:00+01:00';
  const dateModified = '2025-06-01T00:00:00+02:00';
  
  // Generate alternate language URL
  const getAlternateUrl = (currentPath, currentLang) => {
    if (!currentPath || currentPath === '/') return '/';
    
    // Check if it's an artwork path
    if (currentPath.startsWith('/werk/') || currentPath.startsWith('/artwork/')) {
      return getAlternateArtworkUrl(currentPath, currentLang);
    }
    
    const alternatePath = pathMappings[currentPath];
    return alternatePath || currentPath;
  };
  
  const currentLang = language || 'de';
  const alternateLang = currentLang === 'de' ? 'en' : 'de';
  const alternateUrl = `${baseUrl}${getAlternateUrl(path, currentLang)}`;
  
  // Build structured data based on page type
  const buildStructuredData = () => {
    // Base WebPage properties
    const baseWebPage = {
      '@context': 'https://schema.org',
      '@type': pageType || 'WebPage',
      '@id': `${url}#webpage`,
      name: title,
      description: description,
      url: url,
      inLanguage: currentLang === 'de' ? 'de-DE' : 'en-US',
      dateCreated: dateCreated,
      dateModified: dateModified,
      author: { '@id': 'https://atelier-wpb.com/#person' },
      publisher: { '@id': 'https://atelier-wpb.com/#person' }
    };
    
    // ProfilePage for /ueber_mich and /about
    if (pageType === 'ProfilePage') {
      return {
        ...baseWebPage,
        '@type': 'ProfilePage',
        mainEntity: artistData
      };
    }
    
    // CollectionPage for /arbeiten and /portfolio
    if (pageType === 'CollectionPage') {
      return {
        ...baseWebPage,
        '@type': 'CollectionPage',
        about: { '@id': 'https://atelier-wpb.com/#person' },
        mainEntity: {
          '@type': 'ItemList',
          name: currentLang === 'de' ? 'Kunstwerke von Waltraud Pfister-Bläske' : 'Artworks by Waltraud Pfister-Bläske',
          description: description,
          itemListElement: []
        }
      };
    }
    
    // ContactPage for /kontakt and /contact
    if (pageType === 'ContactPage') {
      return {
        ...baseWebPage,
        '@type': 'ContactPage',
        about: { '@id': 'https://atelier-wpb.com/#person' },
        mainEntity: {
          '@type': 'Organization',
          name: 'Atelier WPB',
          url: baseUrl,
          email: 'atelier.wpb.feldafing@gmail.com',
          address: artistData.address
        }
      };
    }
    
    // AboutPage for legal pages (impressum, datenschutz)
    if (pageType === 'AboutPage') {
      return {
        ...baseWebPage,
        '@type': 'AboutPage',
        about: { '@id': 'https://atelier-wpb.com/#person' }
      };
    }
    
    // VisualArtwork for individual artwork pages
    if (pageType === 'VisualArtwork') {
      return {
        '@context': 'https://schema.org',
        '@type': 'VisualArtwork',
        name: title,
        description: description,
        url: url,
        image: imageUrl || defaultImage,
        creator: artistData,
        dateCreated: datePublished || dateCreated,
        artMedium: 'Various media',
        artform: 'Fine art',
        inLanguage: currentLang === 'de' ? 'de-DE' : 'en-US'
      };
    }
    
    // Default WebPage
    return baseWebPage;
  };
  
  // LocalBusiness/Artist structured data for the main page
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${baseUrl}/#localbusiness`,
    name: 'Atelier WPB – Waltraud Pfister-Bläske',
    description: 'Abstrakte Malerei zwischen Starnberger See und Italien',
    url: baseUrl,
    image: defaultImage,
    priceRange: '$$',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Feldafing',
      addressRegion: 'Bayern',
      addressCountry: 'DE'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 47.9442,
      longitude: 11.2942
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '10:00',
      closes: '18:00'
    }
  };
  
  // Person schema (for graph linking)
  const personSchema = {
    '@context': 'https://schema.org',
    ...artistData
  };
  
  // Build the structured data
  const pageSchema = buildStructuredData();
  
  // Combine schemas: all pages get Person schema, main pages also get LocalBusiness
  let combinedSchema;
  if (path === '/' || !path) {
    combinedSchema = [pageSchema, localBusinessSchema, personSchema];
  } else if (pageType === 'ProfilePage') {
    // ProfilePage already has mainEntity with Person, but add LocalBusiness
    combinedSchema = [pageSchema, localBusinessSchema];
  } else {
    combinedSchema = [pageSchema, personSchema];
  }
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={currentLang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={url} />
      
      {/* Hreflang Tags for Multilingual SEO */}
      <link rel="alternate" hreflang={currentLang === 'de' ? 'de-DE' : 'en-US'} href={url} />
      <link rel="alternate" hreflang={alternateLang === 'de' ? 'de-DE' : 'en-US'} href={alternateUrl} />
      <link rel="alternate" hreflang="x-default" href={baseUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl || defaultImage} />
      <meta property="og:locale" content={currentLang === 'de' ? 'de_DE' : 'en_US'} />
      <meta property="og:locale:alternate" content={alternateLang === 'de' ? 'de_DE' : 'en_US'} />
      <meta property="og:site_name" content="Atelier WPB" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl || defaultImage} />
      
      {/* Structured Data */}
      <script type="application/ld+json">{JSON.stringify(combinedSchema)}</script>
    </Helmet>
  );
}

export default SEOHelmet;